import React from 'react';
import { gql, useQuery } from '@apollo/client';
import { NavLink, Navigate } from 'react-router-dom';

import { OdrLoadingSpinner } from '@/components/ui/spinner';
import { useUser } from '@/store/useUser';
import { hasAnyPolicy } from '@/utils/policies';
import { normalizeValue } from './EcmDashboardShared';

import { EcmDashboardCensusView } from './EcmDashboardCensusView';
import {
  EcmDashboardCalaimDemographicsView,
  EcmDashboardCensusLockView,
  EcmDashboardDischargeView,
  EcmDashboardDocumentationView,
  EcmDashboardExceptionsView,
  EcmDashboardLegacyDashboardView,
  EcmDashboardOutreachView,
  EcmDashboardProviderMedicalDemographicsView,
} from './EcmDashboardDataViews';

const ECM_DASHBOARD_QUERY = gql`
  query EcmDashboard {
    ecmDashboard {
      canViewCalaimDemographics
      canViewCensus
      canViewCensusLock
      canViewDischarge
      canViewDocumentation
      canViewExceptions
      canViewLegacyDashboard
      canViewMifTools
      canViewOutreach
      canViewProviderMedicalDemographics
      defaultLandingPath
      diversionUserId
      email
      fullName
      hasSuperAccess
      isAdminUser
      pageTitle
      routeBase
      username
    }
  }
`;

type EcmDashboardData = {
  canViewCalaimDemographics: boolean;
  canViewCensus: boolean;
  canViewCensusLock: boolean;
  canViewDischarge: boolean;
  canViewDocumentation: boolean;
  canViewExceptions: boolean;
  canViewLegacyDashboard: boolean;
  canViewMifTools: boolean;
  canViewOutreach: boolean;
  canViewProviderMedicalDemographics: boolean;
  defaultLandingPath: string;
  diversionUserId: number;
  email: string;
  fullName: string;
  hasSuperAccess: boolean;
  isAdminUser: boolean;
  pageTitle: string;
  routeBase: string;
  username: string;
};

type EcmDashboardQueryResult = {
  ecmDashboard: EcmDashboardData;
};

type EcmDashboardView =
  | 'census'
  | 'exceptions'
  | 'documentation'
  | 'outreach'
  | 'discharge'
  | 'provider-medical-demographics'
  | 'calaim-demographics'
  | 'census-lock'
  | 'legacy-dashboard';

const hasEcmDashboardAccess = (user: any) => {
  return hasAnyPolicy(user, ['tools:billing:access~full', 'tools:billing:admin~full', 'billing:ecm:dashboard~read']);
};

const navClassName = ({ isActive }: { isActive: boolean }) => {
  if (isActive) {
    return 'inline-flex h-8 items-center rounded-md border border-slate-900 bg-slate-900 px-3 text-xs font-medium text-white';
  }

  return 'inline-flex h-8 items-center rounded-md border border-slate-300 bg-white px-3 text-xs font-medium text-slate-700 transition-colors hover:border-slate-400 hover:text-slate-900';
};

const buildNavItems = (dashboard: EcmDashboardData) => {
  const navItems = [
    { label: 'Census', path: `${dashboard.routeBase}/census` },
    dashboard.canViewLegacyDashboard ? { label: 'Legacy ECM Dashboard', path: `${dashboard.routeBase}/legacy-dashboard` } : null,
    dashboard.canViewCalaimDemographics ? { label: 'CalAIM Demographics', path: `${dashboard.routeBase}/calaim-demographics` } : null,
    dashboard.canViewProviderMedicalDemographics
      ? { label: 'Provider Medi-Cal Demographics', path: `${dashboard.routeBase}/provider-medical-demographics` }
      : null,
    dashboard.canViewExceptions ? { label: 'Exceptions', path: `${dashboard.routeBase}/exceptions` } : null,
    dashboard.canViewDocumentation ? { label: 'Documentation', path: `${dashboard.routeBase}/documentation` } : null,
    dashboard.canViewOutreach ? { label: 'Outreach', path: `${dashboard.routeBase}/outreach` } : null,
    dashboard.canViewDischarge ? { label: 'Discharge', path: `${dashboard.routeBase}/discharge` } : null,
    dashboard.canViewCensusLock ? { label: 'Census Lock', path: `${dashboard.routeBase}/census-lock` } : null,
  ];

  return navItems.filter((item): item is { label: string; path: string } => Boolean(item));
};

const canOpenView = (dashboard: EcmDashboardData, view: EcmDashboardView) => {
  if (view === 'census') {
    return dashboard.canViewCensus;
  }

  if (view === 'exceptions') {
    return dashboard.canViewExceptions;
  }

  if (view === 'documentation') {
    return dashboard.canViewDocumentation;
  }

  if (view === 'outreach') {
    return dashboard.canViewOutreach;
  }

  if (view === 'discharge') {
    return dashboard.canViewDischarge;
  }

  if (view === 'calaim-demographics') {
    return dashboard.canViewCalaimDemographics;
  }

  if (view === 'provider-medical-demographics') {
    return dashboard.canViewProviderMedicalDemographics;
  }

  if (view === 'legacy-dashboard') {
    return dashboard.canViewLegacyDashboard;
  }

  if (view === 'census-lock') {
    return dashboard.canViewCensusLock;
  }

  return true;
};

const AccessState = ({ title, description }: { title: string; description: string }) => {
  return (
    <div className="space-y-3 p-6">
      <div className="rounded-lg border border-slate-200 bg-white px-4 py-4 shadow-sm">
        <div className="text-sm font-medium text-slate-900">{title}</div>
        <div className="mt-1 text-sm text-slate-600">{description}</div>
      </div>
    </div>
  );
};

const renderDashboardView = (
  view: EcmDashboardView,
  options: {
    canManageParticipants: boolean;
    readOnlyCensus: boolean;
  }
) => {
  if (view === 'census') {
    return <EcmDashboardCensusView readOnly={options.readOnlyCensus} />;
  }

  if (view === 'exceptions') {
    return <EcmDashboardExceptionsView canManageParticipants={options.canManageParticipants} />;
  }

  if (view === 'documentation') {
    return <EcmDashboardDocumentationView canManageParticipants={options.canManageParticipants} />;
  }

  if (view === 'outreach') {
    return <EcmDashboardOutreachView canManageParticipants={options.canManageParticipants} />;
  }

  if (view === 'discharge') {
    return <EcmDashboardDischargeView />;
  }

  if (view === 'provider-medical-demographics') {
    return <EcmDashboardProviderMedicalDemographicsView />;
  }

  if (view === 'calaim-demographics') {
    return <EcmDashboardCalaimDemographicsView />;
  }

  if (view === 'census-lock') {
    return <EcmDashboardCensusLockView canLockCensus={options.canManageParticipants} />;
  }

  return <EcmDashboardLegacyDashboardView />;
};

const EcmDashboardShell = ({
  dashboard,
  view,
  readOnlyCensus,
}: {
  dashboard: EcmDashboardData;
  view: EcmDashboardView;
  readOnlyCensus: boolean;
}) => {
  const navItems = buildNavItems(dashboard);
  const canManageParticipants = Boolean(dashboard.hasSuperAccess || dashboard.isAdminUser);

  return (
    <div className="space-y-3 p-6">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-2">
        {navItems.map((item) => (
          <NavLink key={item.path} className={navClassName} to={item.path}>
            {item.label}
          </NavLink>
        ))}
      </div>
      {renderDashboardView(view, { canManageParticipants, readOnlyCensus })}
    </div>
  );
};

const EcmDashboardRoutePage = ({ view }: { view: EcmDashboardView }) => {
  const { user, isUserLoading } = useUser();
  const canAccess = hasEcmDashboardAccess(user);
  const { data, error, loading } = useQuery<EcmDashboardQueryResult>(ECM_DASHBOARD_QUERY, {
    fetchPolicy: 'cache-and-network',
    skip: !canAccess,
  });

  if (isUserLoading || (canAccess && loading)) {
    return <OdrLoadingSpinner />;
  }

  if (!canAccess) {
    return (
      <AccessState
        title="Access Denied"
        description="ECM Dashboard requires ECM Dashboard access."
      />
    );
  }

  if (error) {
    if (normalizeValue(error.message).includes('unauthorized')) {
      return (
        <AccessState
          title="Access Denied"
          description="ECM Dashboard requires ECM Dashboard access."
        />
      );
    }

    return (
      <AccessState
        title="Unable to Open ECM Dashboard"
        description={error.message}
      />
    );
  }

  const dashboard = data?.ecmDashboard;
  if (!dashboard) {
    return (
      <AccessState
        title="Unable to Open ECM Dashboard"
        description="ECM Dashboard could not be loaded."
      />
    );
  }

  if (!canOpenView(dashboard, view)) {
    return <Navigate to={dashboard.defaultLandingPath} replace />;
  }

  return <EcmDashboardShell dashboard={dashboard} view={view} readOnlyCensus={!hasAnyPolicy(user, ['tools:billing:access~full'])} />;
};

export const EcmDashboardLandingRedirect = () => {
  return <Navigate to="/tools/ecm-dashboard/census" replace />;
};

export const EcmDashboardCensusPage = () => {
  return <EcmDashboardRoutePage view="census" />;
};

export const EcmDashboardExceptionsPage = () => {
  return <EcmDashboardRoutePage view="exceptions" />;
};

export const EcmDashboardDocumentationPage = () => {
  return <EcmDashboardRoutePage view="documentation" />;
};

export const EcmDashboardOutreachPage = () => {
  return <EcmDashboardRoutePage view="outreach" />;
};

export const EcmDashboardDischargePage = () => {
  return <EcmDashboardRoutePage view="discharge" />;
};

export const EcmDashboardProviderMedicalDemographicsPage = () => {
  return <EcmDashboardRoutePage view="provider-medical-demographics" />;
};

export const EcmDashboardCalaimDemographicsPage = () => {
  return <EcmDashboardRoutePage view="calaim-demographics" />;
};

export const EcmDashboardCensusLockPage = () => {
  return <EcmDashboardRoutePage view="census-lock" />;
};

export const EcmDashboardLegacyDashboardPage = () => {
  return <EcmDashboardRoutePage view="legacy-dashboard" />;
};
